/**
 * G3 카드 처리 목 상태 — 소각 교환 / 재매입 / 자산 출금 / KYC (서버 RPC 부재).
 * TODO(server): 대응 RPC(buyback_card, request_redemption, kyc_status …)가
 * 생기는 즉시 이 파일 전체를 서버 호출로 교체한다. 값 산정식(기준 시세·비율·
 * 카드 IP)은 전부 목 — 화면에는 "server value" 표기를 병기한다.
 * 규약: lib/renewal/mockdb.ts 는 읽기 전용 공유 상수 — 화면 상태는 여기서 관리.
 */
import type { CardFinish } from "@/lib/api";
import { finishMeta } from "@/lib/cards";
import {
  BUYBACK_RATE_RANGE,
  mockDelay,
  type KycStatus,
  type RedemptionStage,
} from "@/lib/renewal/mockdb";

// ── 파생 목 값 ──────────────────────────────────────────────────────────

/** 카드 IP 목 — pullIp 기반(§3.3.2 벤치마크 Base=1,000에 base pullIp 40을 맞춘 ×25). */
export function mockCardIp(finish: string): number {
  return finishMeta(finish).pullIp * 25;
}

/** 프리미엄(재매입 5필드 대상) = 시리얼 캡 티어 전부. */
export function isPremiumFinish(finish: string): boolean {
  return finishMeta(finish).supplyCap != null;
}

/** 자산 연계 티어 목 — 최상위 4티어(supplyCap ≤ 10)만 자산 연계로 취급. */
export function isAssetLinked(finish: string): boolean {
  const cap = finishMeta(finish).supplyCap;
  return cap != null && cap <= 10;
}

export type AssetLink = {
  assetKind: "BTC" | "ETH";
  quantity: number;
  /** 기준 가격 (USDT, 자산 1단위 기준) */
  basisUsdt: number;
  basisAppliedAt: string;
  vault: string;
  withdrawable: boolean;
  feeNote: string;
};

const ASSET_LINKS: Partial<Record<CardFinish, AssetLink>> = {
  red_ice_sapphire: { assetKind: "ETH", quantity: 0.05, basisUsdt: 3200, basisAppliedAt: "2026-08-01 00:00 UTC", vault: "TycoonEX Asset Vault #1", withdrawable: true, feeNote: "Network + processing fee (exchange-level)" },
  black_ice_prizms: { assetKind: "ETH", quantity: 0.2, basisUsdt: 3200, basisAppliedAt: "2026-08-01 00:00 UTC", vault: "TycoonEX Asset Vault #1", withdrawable: true, feeNote: "Network + processing fee (exchange-level)" },
  padparadscha_sapphire: { assetKind: "BTC", quantity: 0.01, basisUsdt: 68000, basisAppliedAt: "2026-08-01 00:00 UTC", vault: "TycoonEX Asset Vault #1", withdrawable: true, feeNote: "Network + processing fee (exchange-level)" },
  gold_vinyl: { assetKind: "BTC", quantity: 0.05, basisUsdt: 68000, basisAppliedAt: "2026-08-01 00:00 UTC", vault: "TycoonEX Asset Vault #1", withdrawable: true, feeNote: "Network + processing fee (exchange-level)" },
};

export function assetLink(finish: string): AssetLink | null {
  return ASSET_LINKS[finish as CardFinish] ?? null;
}

export type BuybackQuote = {
  /** 플랫폼 기준 시세 (RP) */
  referenceRp: number;
  /** 재매입 비율 (mock 0.85 — 서버 값 표기 필수) */
  rate: number;
  /** 예상 지급 RP */
  expectedRp: number;
  /** 제거될 IP (1장 기준) */
  removedIp: number;
  /** 자산 신청 가능 여부 */
  redemptionEligible: boolean;
};

/** 기준 시세 목 — 자산 연계면 연계 자산 가치(100 RP = 1 USDT), 아니면 pullIp×100. */
export function buybackQuote(finish: string): BuybackQuote {
  const link = assetLink(finish);
  const referenceRp = link
    ? Math.round(link.quantity * link.basisUsdt * 100)
    : finishMeta(finish).pullIp * 100;
  const rate = BUYBACK_RATE_RANGE.mock;
  return {
    referenceRp,
    rate,
    expectedRp: Math.round(referenceRp * rate),
    removedIp: mockCardIp(finish),
    redemptionEligible: isAssetLinked(finish),
  };
}

// ── localStorage 상태 ───────────────────────────────────────────────────

export type ProcessingState =
  | { kind: "buyback"; completedAt: number }
  | { kind: "redemption"; redemptionId: string };

export type RedemptionRequest = {
  id: string;
  catalogId: string;
  agentId: string;
  finish: CardFinish;
  requestedAt: number;
};

type Store = {
  kyc: { status: KycStatus; startedAt: number | null };
  /** catalogId → 진행/완료 처리 (R-09 배타 잠금의 근거) */
  processing: Record<string, ProcessingState>;
  redemptions: RedemptionRequest[];
  rpBalance: number;
};

const STORE_KEY = "ty.renewal.cardActions.v1";
const EMPTY: Store = { kyc: { status: "none", startedAt: null }, processing: {}, redemptions: [], rpBalance: 0 };

function loadStore(): Store {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return structuredClone(EMPTY);
    return { ...structuredClone(EMPTY), ...(JSON.parse(raw) as Partial<Store>) };
  } catch {
    return structuredClone(EMPTY);
  }
}

function saveStore(store: Store) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    // private mode 등 저장 불가 — 목 상태는 세션 한정으로 동작
  }
}

// ── KYC (none → pending → verified, mock 벤더 15초 자동 승인) ───────────

const KYC_REVIEW_MS = 15_000;

export function getKycStatus(): KycStatus {
  const store = loadStore();
  if (
    store.kyc.status === "pending" &&
    store.kyc.startedAt != null &&
    Date.now() - store.kyc.startedAt > KYC_REVIEW_MS
  ) {
    // ponytail: 시간 기반 목 승인 — 서버 KYC 웹훅이 생기면 제거
    store.kyc = { status: "verified", startedAt: store.kyc.startedAt };
    saveStore(store);
  }
  return store.kyc.status;
}

export function startKyc(): KycStatus {
  const store = loadStore();
  if (store.kyc.status === "none" || store.kyc.status === "rejected") {
    store.kyc = { status: "pending", startedAt: Date.now() };
    saveStore(store);
  }
  return store.kyc.status;
}

// ── 처리 배타 상태 ──────────────────────────────────────────────────────

export function getProcessing(catalogId: string): ProcessingState | null {
  return loadStore().processing[catalogId] ?? null;
}

/** 재매입 확정 — 목 실행: 처리 잠금 + RP 잔액 반영. */
export async function confirmBuyback(
  catalogId: string,
  expectedRp: number,
): Promise<{ rpBalance: number }> {
  await mockDelay();
  const store = loadStore();
  if (store.processing[catalogId]) throw new Error("Another processing is already in progress for this card.");
  store.processing[catalogId] = { kind: "buyback", completedAt: Date.now() };
  store.rpBalance += expectedRp;
  saveStore(store);
  return { rpBalance: store.rpBalance };
}

/** 자산 출금 신청 — 목 실행: 배타 잠금 발동 + 추적 목록 등록. */
export async function submitRedemption(
  catalogId: string,
  agentId: string,
  finish: CardFinish,
): Promise<RedemptionRequest> {
  await mockDelay();
  const store = loadStore();
  if (store.processing[catalogId]) throw new Error("Another processing is already in progress for this card.");
  const request: RedemptionRequest = {
    id: `rd-${Date.now().toString(36)}`,
    catalogId,
    agentId,
    finish,
    requestedAt: Date.now(),
  };
  store.processing[catalogId] = { kind: "redemption", redemptionId: request.id };
  store.redemptions.unshift(request);
  saveStore(store);
  return request;
}

export const REDEMPTION_STAGES: RedemptionStage[] = [
  "requested",
  "processing",
  "transferred",
  "burned",
];

/**
 * 단계 목 — 경과 시간으로 전진 (신청 1분→처리, 3분→전송, 6분→소각).
 * TODO(server): 서버 상태 그대로 표시로 교체 — UI 추정 금지(UF-7 E2).
 */
export function redemptionStage(request: RedemptionRequest): RedemptionStage {
  const elapsed = Date.now() - request.requestedAt;
  if (elapsed > 360_000) return "burned";
  if (elapsed > 180_000) return "transferred";
  if (elapsed > 60_000) return "processing";
  return "requested";
}

/** redemptionStage와 동일 오프셋 — 단계 도달 시각 목. TODO(server): 서버 타임스탬프로 교체. */
const STAGE_OFFSET_MS: Record<RedemptionStage, number> = {
  requested: 0,
  processing: 60_000,
  transferred: 180_000,
  burned: 360_000,
};

/** 도달한 단계만 타임스탬프 보유 — 미도달 단계는 키 없음(화면에서 — 처리). */
export function redemptionTimestamps(
  request: RedemptionRequest,
): Partial<Record<RedemptionStage, number>> {
  const elapsed = Date.now() - request.requestedAt;
  const out: Partial<Record<RedemptionStage, number>> = {};
  for (const stage of REDEMPTION_STAGES) {
    if (elapsed >= STAGE_OFFSET_MS[stage]) out[stage] = request.requestedAt + STAGE_OFFSET_MS[stage];
  }
  return out;
}

export function listRedemptions(): RedemptionRequest[] {
  return loadStore().redemptions;
}

export function getRedemption(id: string): RedemptionRequest | null {
  return loadStore().redemptions.find((r) => r.id === id) ?? null;
}
